import React from "react";
import { Box, CircularProgress, Typography, Fade } from "@mui/material";
import { alpha } from "@mui/material/styles";
import SupplyChainBackdrop from "./SupplyChainBackdrop";

const Ring = ({ size = 56, thickness = 3.5 }) => (
    <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress
            variant="determinate"
            value={100}
            size={size}
            thickness={thickness}
            sx={(theme) => ({
                color: alpha(theme.palette.primary.main, 0.12),
            })}
        />
        <CircularProgress
            variant="indeterminate"
            size={size}
            thickness={thickness}
            disableShrink
            sx={{
                position: "absolute",
                left: 0,
                color: "primary.main",
                animationDuration: "900ms",
                "& .MuiCircularProgress-circle": {
                    strokeLinecap: "round",
                },
            }}
        />
    </Box>
);

/**
 * Loader with three usage modes:
 *   <Spinner />                            full-page (default), e.g. initial app load
 *   <Spinner variant="inline" />           centered in its container, for page/card loading
 *   <Spinner variant="overlay" />          covers parent (parent must be position: relative), for refetches
 *
 * Optional: label, size, thickness.
 */
const Spinner = ({
    variant = "fullscreen",
    label,
    size = 56,
    thickness = 3.5,
}) => {
    const content = (
        <Fade in timeout={250}>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1.5,
                }}
            >
                <Ring size={size} thickness={thickness} />
                {label && (
                    <Typography variant="body2" color="text.secondary">
                        {label}
                    </Typography>
                )}
            </Box>
        </Fade>
    );

    if (variant === "fullscreen") {
        return (
            <Box
                sx={(theme) => ({
                    position: "fixed",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    background: `
                        radial-gradient(circle at 12% 18%, ${alpha(
                            theme.palette.primary.main,
                            theme.palette.mode === "dark" ? 0.18 : 0.1
                        )} 0%, transparent 45%),
                        radial-gradient(circle at 88% 82%, ${alpha(
                            theme.palette.secondary.main,
                            theme.palette.mode === "dark" ? 0.16 : 0.08
                        )} 0%, transparent 45%),
                        ${theme.palette.background.default}
                    `,
                    zIndex: theme.zIndex.modal + 1,
                })}
            >
                <SupplyChainBackdrop
                    opacity={0.45}
                    animate
                />
                <Box sx={{ position: "relative", zIndex: 1 }}>{content}</Box>
            </Box>
        );
    }

    if (variant === "overlay") {
        return (
            <Box
                sx={(theme) => ({
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: alpha(theme.palette.background.paper, 0.6),
                    backdropFilter: "blur(2px)",
                    borderRadius: "inherit",
                    zIndex: 1,
                })}
            >
                {content}
            </Box>
        );
    }

    // inline
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: { xs: 4, md: 6 },
            }}
        >
            {content}
        </Box>
    );
};

export default Spinner;